import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { Button, Card, CardBody, Input, Sheet, Spinner, Stub, Textarea, Toast } from "@/components/ui"
import type { PromptTemplateOut } from "@/lib/types"
import { PencilIcon, PlusIcon, Trash2Icon, WandIcon } from "lucide-react"

export const Route = createFileRoute("/_app/prompts")({
  component: PromptsPage,
})

function PromptsPage() {
  const me = auth.me()
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["prompts", me?.id],
    queryFn: () => api<PromptTemplateOut[]>(`/api/dashboard/users/${me?.id}/prompt_templates`),
    enabled: !!me?.id,
  })
  const [editing, setEditing] = useState<PromptTemplateOut | null>(null)
  const [adding, setAdding] = useState(false)

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/prompt_templates/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prompts"] }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Prompt library</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5">
            Reusable prompts. Type <code>/slug</code> in chat to inline them.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}><PlusIcon className="h-4 w-4" /> New prompt</Button>
      </div>

      {list.isLoading && <Spinner />}
      {list.data?.length === 0 && <Stub title="No prompts yet" hint="Save your favourite recipes — meeting recap, weekly review, etc." />}

      <div className="grid gap-2">
        {list.data?.map((p) => (
          <Card key={p.id}>
            <CardBody className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <WandIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm font-medium">{p.title}</div>
                  <code className="text-[10px] text-muted-foreground">/{p.slug}</code>
                </div>
                <pre className="mt-2 text-[11px] text-muted-foreground whitespace-pre-wrap line-clamp-3 font-mono">{p.body}</pre>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => setEditing(p)}>
                  <PencilIcon className="h-4 w-4 text-muted-foreground" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => {
                  if (window.confirm(`Delete prompt "${p.title}"?`)) del.mutate(p.id)
                }}>
                  <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <PromptSheet open={adding} onClose={() => { setAdding(false); qc.invalidateQueries({ queryKey: ["prompts"] }) }} />
      <PromptSheet open={!!editing} editing={editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["prompts"] }) }} />
    </div>
  )
}

function PromptSheet({
  open, onClose, editing,
}: { open: boolean; onClose: () => void; editing?: PromptTemplateOut | null }) {
  const me = auth.me()
  const [slug, setSlug] = useState(editing?.slug ?? "")
  const [title, setTitle] = useState(editing?.title ?? "")
  const [body, setBody] = useState(editing?.body ?? "")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function save() {
    if (!me?.id) return
    setBusy(true); setErr(null)
    try {
      if (editing) {
        await api(`/api/dashboard/prompt_templates/${editing.id}`, { method: "PATCH", body: { slug, title, body } })
      } else {
        await api(`/api/dashboard/users/${me.id}/prompt_templates`, { method: "POST", body: { slug, title, body } })
      }
      onClose()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={editing ? "Edit prompt" : "New prompt"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !slug || !title || !body}>{busy ? <Spinner /> : null}Save</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FieldRow label="Slug"><Input value={slug} onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9_-]/gi, ""))} placeholder="meeting-recap" /></FieldRow>
        <FieldRow label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting recap" /></FieldRow>
        <FieldRow label="Body"><Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Summarize the following meeting transcript…" /></FieldRow>
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  )
}
